import { useState } from "react"
import { toast } from "react-toastify"
import styled from "styled-components"

import { useActivateClientMutation, useLazyGetArchivedClientsQuery, useLazyGetClientsQuery } from "store/api/clientApi"
import { ApiTypes, UITypes } from "types"
import styles from "assets/scss/index.module.scss"
import { ReactComponent as IconArrowPath } from "assets/svg/arrow-path.svg"

import { convertClientsToTableRows } from "utils/convertData"
import useExportCSV from "hooks/useExportCSV"
import ConfirmationModal from "modals/ConfirmationModal/ConfirmationModal"
import ExportButton from "components/common/ExportButton"
import EditColumnsButton from "components/EditColumnsButton"
import { CheckboxNew } from "components/form/CheckboxNew"

const StyledNavWrapper = styled.nav`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  width: 100%;
`

const StyledTableNavButtonsWrapper = styled.div`
  flex: 1;
`

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  flex: 1;
  flex-wrap: wrap;
`

const RestoreCustomerButton = styled.div<{ disabled: boolean }>`
  display: flex;
  margin-left: 10px;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  color: var(--accent);
  margin-right: auto;
  cursor: ${props => (props.disabled ? "initial" : "pointer")};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
`

const IconContainer = styled.span`
  display: inline-block;
  width: 44px;
  height: 44px;
  padding-top: 10px;
  background-color: var(--accent-subtle);
  border-radius: 10px;

  svg {
    width: 24px;
    height: 24px;
  }
`

interface CustomersPageHeaderProps {
  showArchived: boolean
  urlQueryParams: UITypes.Params
  tableHeaderCells: UITypes.TableHeaderCell[]
  data: ApiTypes.Res.Clients | undefined
  onShowArchivedChange: (showArchived: boolean) => void
  onActivateSuccess: () => void
  selectedCustomer: number[]
}

const CustomersPageHeader = ({
  showArchived,
  urlQueryParams,
  tableHeaderCells,
  data,
  selectedCustomer,
  onShowArchivedChange,
  onActivateSuccess,
}: CustomersPageHeaderProps) => {
  const [showRestoreCustomerModal, setShowRestoreCustomerModal] = useState(false)
  const [activateClient] = useActivateClientMutation()
  const { handleExport: handleExportClients } = useExportCSV({
    params: urlQueryParams,
    headerCells: tableHeaderCells,
    meta: data?.meta,
    fileName: "customers",
    convertData: convertClientsToTableRows,
    useLazyQuery: useLazyGetClientsQuery,
  })
  const { handleExport: handleExportArchivedClients } = useExportCSV({
    params: urlQueryParams,
    headerCells: tableHeaderCells,
    meta: data?.meta,
    fileName: "archived-customers",
    convertData: convertClientsToTableRows,
    useLazyQuery: useLazyGetArchivedClientsQuery,
  })
  const handleExport = showArchived ? handleExportArchivedClients : handleExportClients

  const handleShowArchivedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onShowArchivedChange(event.target.checked)
  }

  const handleShowRestoreCustomerModal = () => {
    if (selectedCustomer.length === 0) return

    setShowRestoreCustomerModal(true)
  }

  const handleRestoreCustomer = async () => {
    if (selectedCustomer.length === 0) return

    try {
      await activateClient(selectedCustomer[0])
      toast.success("Customer restored successfully")
      onActivateSuccess()
    } catch (e) {
      const error = e as Error
      toast.error(error.message)
    }
    setShowRestoreCustomerModal(false)
  }

  return (
    <>
      <StyledNavWrapper>
        <StyledTableNavButtonsWrapper className={styles.tableNavButtonsWrapper}>
          <ButtonsContainer>
            {showArchived && (
              <RestoreCustomerButton disabled={selectedCustomer.length === 0} onClick={handleShowRestoreCustomerModal}>
                <IconArrowPath /> Restore Customer
              </RestoreCustomerButton>
            )}
            <EditColumnsButton tableName="contractor_customers_list" tableHeaderCells={tableHeaderCells} />
            <CheckboxNew
              className={styles.tableNavItem}
              isReverse={true}
              id="fulfilled"
              onChange={handleShowArchivedChange}
              isChecked={showArchived}
            >
              Show Archived
            </CheckboxNew>
            <ExportButton onClick={handleExport} disabled={data?.meta?.total === 0} />
          </ButtonsContainer>
        </StyledTableNavButtonsWrapper>
      </StyledNavWrapper>
      {showRestoreCustomerModal && (
        <ConfirmationModal
          title="Restore Customer"
          message="Are you sure you want to restore the customer?"
          icon={
            <IconContainer>
              <IconArrowPath />
            </IconContainer>
          }
          onCancel={() => setShowRestoreCustomerModal(false)}
          onSubmit={handleRestoreCustomer}
        />
      )}
    </>
  )
}

export default CustomersPageHeader
